import React, { createContext, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useBlocker, useNavigate } from 'react-router';

export const BlockerContext = createContext();

export const useBlockerRegistration = (condition, onBlock) => {
  const ctx = useContext(BlockerContext);
  const registerBlocker = useMemo(() => ctx?.registerBlocker || (() => { }), [ctx?.registerBlocker]);
  const unregisterBlocker = useMemo(() => ctx?.unregisterBlocker || (() => { }), [ctx?.unregisterBlocker])
  const blocker = useRef({});
  const nav = useRef(null);

  const setNavigate = useCallback((newNavigate) => {
    nav.current = newNavigate
  }, [])

  const proceedBlocker = useCallback(() => {
    if (nav.current) {
      nav.current();
    }
  }, [])

  useEffect(() => {
    blocker.current = { condition, onBlock, setNavigate }
  }, [condition, onBlock, setNavigate]);

  const getBlocker = useCallback(() => blocker.current, []);

  useEffect(() => {
    const id = registerBlocker(getBlocker);
    return () => {
      unregisterBlocker(id);
    };
  }, [registerBlocker, unregisterBlocker, getBlocker]);

  return proceedBlocker;
}

export const useRouteBlocker = () => {
  const blocker = useBlocker((tx) => {
    if (tx.nextLocation?.state?.skipBlocker) return false;
    return true
  });

  const blockerCtx = useContext(BlockerContext);
  const getBlockers = useMemo(() => blockerCtx?.getBlockers || (() => { }), [blockerCtx.getBlockers])

  const navigate = useNavigate();
  const blockedLocation = useRef(null);

  const proceed = useCallback(() => {
    const nextLocation = blockedLocation.current
    navigate(nextLocation.pathname, { state: { ...nextLocation.state, skipBlocker: true } });
  }, [navigate])

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const newLocation = blocker.location;
      blockedLocation.current = newLocation;
      const blockers = getBlockers();

      for (let i = 0; i < blockers.length; i++) {
        const b = blockers[i];
        if (b.condition(newLocation)) {
          b.setNavigate(proceed);
          blocker.reset();
          b.onBlock(newLocation);
          return;
        }
      }
      blocker.proceed();
    }
  }, [blocker, getBlockers, proceed]);
}

export const useRequestNavigation = () => {
  const blockerCtx = useContext(BlockerContext);

  return (to, onProceed) => {
    if (!blockerCtx || !blockerCtx.getBlockers) {
      onProceed();
      return;
    }
    const blockers = blockerCtx.getBlockers();
    const location = typeof to === 'string' ? { pathname: to } : to;
    let blocked = false;
    for (let i = 0; i < blockers.length; i++) {
      const b = blockers[i];
      if (b.condition && b.condition(location)) {
        blocked = true;
        b.setNavigate(() => onProceed());
        b.onBlock(location);
        break;
      }
    }
    if (!blocked) {
      onProceed();
    }
  };
};

const BlockerProvider = ({ children }) => {
  const blockers = useRef([]);
  const setBlockers = (newState) => { blockers.current = newState };

  const registerBlocker = useCallback((getBlocker) => {
    const id = Date.now() + Math.random();
    setBlockers([{ id, getBlocker }, ...blockers.current]);
    return id;
  }, []);

  const unregisterBlocker = useCallback((id) => {
    setBlockers(blockers.current.filter(b => b.id !== id));
  }, []);

  const getBlockers = useCallback(() => blockers.current.map(x => ({ ...x.getBlocker() })), []);

  return (
    <BlockerContext.Provider value={{ registerBlocker, unregisterBlocker, getBlockers }}>
      {children}
    </BlockerContext.Provider>
  );
}


export default BlockerProvider